import { useLazyQuery } from "@apollo/client/react";
import { graphql } from "gql.tada";
import { useEffect, useState } from "react";
import { usePlayers } from "./players.hook";

const USER_GROUPS = graphql(`
  query userGroups($ids: [ID!]!, $page: PageInfo!) {
    userGroups(query: { ids: $ids }, page: $page) {
      nodes {
        id
        managerId
      }
    }
  }
`);

const USER_GROUP_MEMBERS = graphql(`
  query userGroupMembers($ids: [ID!]!, $page: PageInfo!) {
    userGroupMembers(groupIds: $ids, page: $page) {
      nodes {
        id
        groupId
        playerProfileId
        status
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`);

export const useUserGroups = ({
  groupIds,
  skip,
  includeMemberProfiles = true,
  includeManagerProfiles = true,
}: {
  groupIds?: string[];
  skip?: boolean;
  includeMemberProfiles?: boolean;
  includeManagerProfiles?: boolean;
}) => {
  const [groups, setGroups] = useState<
    {
      id: unknown;
      managerId: unknown;
      managerProfile?: ReturnType<typeof usePlayers>["results"][number];
      members: {
        status: string;
        id: unknown;
        playerProfileId: unknown;
        playerProfile?: ReturnType<typeof usePlayers>["results"][number];
      }[];
    }[]
  >([]);
  const [loading, setLoading] = useState(!skip);
  const { refetch: fetchPlayerProfiles } = usePlayers({
    skip: true,
    playerIds: [],
  });

  const [fetch] = useLazyQuery<any>(USER_GROUPS);
  const [fetchGroupMembers] = useLazyQuery<any>(USER_GROUP_MEMBERS);

  const refetch = async (groupIds: string[]) => {
    const result: typeof groups = [];
    while (groupIds.length > 0) {
      const groupIdsSpliced = groupIds.splice(0, 100);
      result.push(
        ...((
          await fetch({
            variables: {
              page: { first: 100 },
              ids: groupIdsSpliced,
            },
          })
        ).data?.userGroups?.nodes.map((group: any) => ({
          ...group,
          members: [],
        })) ?? []),
      );

      if (includeManagerProfiles) {
        const managerProfileIds = new Set(
          result.map((group) => group.managerId as string),
        );
        if (managerProfileIds.size > 0) {
          const managerProfiles = await fetchPlayerProfiles(
            Array.from(managerProfileIds),
          );
          result.forEach((group) => {
            group.managerProfile = managerProfiles?.find(
              (manager) => manager.id === group.managerId,
            );
          });
        }
      }

      let nextMembersCursor: string | boolean = true;
      while (nextMembersCursor) {
        const { data: groupMembersData } = await fetchGroupMembers({
          variables: {
            ids: groupIdsSpliced,
            page: {
              first: 100,
              after: nextMembersCursor !== true ? nextMembersCursor : null,
            },
          },
        });

        const playerProfileIds = new Set(
          groupMembersData?.userGroupMembers.nodes.map(
            (member: any) => member.playerProfileId as string,
          ),
        );
        if (playerProfileIds.size > 0) {
          let playerProfiles: ReturnType<typeof usePlayers>["results"] = [];
          if (includeMemberProfiles) {
            playerProfiles = await fetchPlayerProfiles(
              Array.from(playerProfileIds) as string[],
            );
          }

          groupMembersData?.userGroupMembers.nodes.forEach((member: any) => {
            const group = result.find((group) => group.id === member.groupId);
            if (group) {
              group.members.push({
                ...member,
                playerProfile: playerProfiles?.find(
                  (player) => player.id === member.playerProfileId,
                ),
              });
            }
          });
        }
        nextMembersCursor = groupMembersData?.userGroupMembers.pageInfo
          .hasNextPage
          ? (groupMembersData?.userGroupMembers.pageInfo.endCursor ?? false)
          : false;
      }
    }
    setGroups(result);
    return result;
  };

  useEffect(() => {
    if (!skip && groupIds) {
      setLoading(true);
      refetch(groupIds).finally(() => setLoading(false));
    }
  }, [skip, groupIds]);

  return {
    results: groups,
    refetch,
    loading: loading,
  };
};
